import { apiClient } from '@/services/api-client';
import type {
  CustomerSubscriptionCheckoutConfirmRequest,
  CustomerSubscriptionCheckoutConfirmResponse,
  CustomerSubscriptionCheckoutContext,
  CustomerSubscriptionCheckoutIntent,
  CustomerSubscriptionCheckoutIntentRequest,
} from '@/types/customer-subscription-checkout';

type UnknownRecord = Record<string, unknown>;

const readRecord = (value: unknown): UnknownRecord =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as UnknownRecord) : {};

const readString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length ? trimmed : null;
};

const readBoolean = (value: unknown): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'true' || normalized === '1';
  }
  return false;
};

const readNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const resolvePayload = (input: unknown): UnknownRecord => {
  const root = readRecord(input);
  const data = readRecord(root.data);
  return Object.keys(data).length ? data : root;
};

const normalizeCheckoutContext = (input: unknown): CustomerSubscriptionCheckoutContext => {
  const payload = resolvePayload(input);
  const attributes = readRecord(payload.attributes);
  const source = Object.keys(attributes).length ? attributes : payload;

  const stripeRecord = readRecord(source.stripe);
  const stripePublishableKey = readString(
    stripeRecord.publishable_key ??
      stripeRecord.publishableKey ??
      source.stripe_publishable_key ??
      source.stripePublishableKey,
  );
  const stripeAccountId = readString(
    stripeRecord.account_id ?? stripeRecord.accountId ?? source.stripe_account_id ?? source.stripeAccountId,
  );
  const stripeAccountType = readString(
    stripeRecord.account_type ?? stripeRecord.accountType ?? source.stripe_account_type ?? source.stripeAccountType,
  );

  const planRecord = readRecord(source.plan);
  const priceRecord = readRecord(planRecord.price);
  const billingAddress = readRecord(source.billing_address ?? source.billingAddress);
  const paymentMethod = readRecord(source.payment_method ?? source.paymentMethod);
  const subscription = readRecord(source.subscription);
  const stripeIntentTypeRaw = readString(source.stripe_intent_type ?? source.stripeIntentType);
  const stripeIntentType = stripeIntentTypeRaw === 'setup' || stripeIntentTypeRaw === 'payment' ? stripeIntentTypeRaw : null;

  const currency = readString(priceRecord.currency) ?? 'USD';
  const amountMajor =
    readNumber(priceRecord.recurring_amount ?? priceRecord.recurringAmount) ??
    readNumber(priceRecord.base_amount ?? priceRecord.baseAmount) ??
    readNumber(priceRecord.amount) ??
    readNumber(priceRecord.price);
  const formattedPrice =
    readString(priceRecord.formatted_price ?? priceRecord.formattedPrice) ??
    (typeof amountMajor === 'number'
      ? (() => {
          try {
            return new Intl.NumberFormat('en', { style: 'currency', currency }).format(amountMajor);
          } catch {
            return `${currency} ${amountMajor.toFixed(2)}`;
          }
        })()
      : null);

  return {
    requiresPayment: readBoolean(source.requires_payment ?? source.requiresPayment),
    stripeIntentType,
    subscription: {
      id: readString(subscription.id),
      status: readString(subscription.status),
    },
    stripe:
      stripePublishableKey || stripeAccountId || stripeAccountType
        ? {
            publishableKey: stripePublishableKey,
            accountId: stripeAccountId,
            accountType: stripeAccountType,
          }
        : null,
    stripePublishableKey,
    stripeAccountId,
    stripeAccountType,
    planSummary: {
      id: readString(planRecord.id),
      productId: readString(planRecord.product_id ?? planRecord.productId),
      name: readString(planRecord.name) ?? '',
      price: typeof amountMajor === 'number' ? Math.round(amountMajor * 100) : null,
      currency,
      billingCycle: readString(priceRecord.billing_cycle_name ?? priceRecord.billingCycleName),
      formattedPrice,
    },
    billingAddress: {
      line1: readString(
        billingAddress.address_line_1 ??
          billingAddress.line_1 ??
          billingAddress.line1 ??
          billingAddress.addressLine1,
      ),
      line2: readString(
        billingAddress.address_line_2 ??
          billingAddress.line_2 ??
          billingAddress.line2 ??
          billingAddress.addressLine2,
      ),
      city: readString(billingAddress.city),
      state: readString(billingAddress.state),
      postalCode: readString(billingAddress.postal_code ?? billingAddress.postalCode),
      country: readString(billingAddress.country),
    },
    paymentMethodLabel:
      readString(source.payment_method_label ?? source.paymentMethodLabel) ??
      readString(paymentMethod.status),
  };
};

const normalizeCheckoutIntent = (input: unknown): CustomerSubscriptionCheckoutIntent => {
  const payload = resolvePayload(input);
  const attributes = readRecord(payload.attributes);
  const source = Object.keys(attributes).length ? attributes : payload;

  const stripeRecord = readRecord(source.stripe);
  const stripePublishableKey = readString(
    stripeRecord.publishable_key ??
      stripeRecord.publishableKey ??
      source.stripe_publishable_key ??
      source.stripePublishableKey,
  );
  const stripeAccountId = readString(
    stripeRecord.account_id ?? stripeRecord.accountId ?? source.stripe_account_id ?? source.stripeAccountId,
  );
  const stripeAccountType = readString(
    stripeRecord.account_type ?? stripeRecord.accountType ?? source.stripe_account_type ?? source.stripeAccountType,
  );

  const stripeIntentTypeRaw = readString(source.stripe_intent_type ?? source.stripeIntentType);
  const stripeIntentType =
    stripeIntentTypeRaw === 'setup' || stripeIntentTypeRaw === 'payment' ? stripeIntentTypeRaw : null;

  return {
    // Backend may return intent_id or use JSON:API resource id.
    purchaseIntentId:
      readString(source.purchase_intent_id ?? source.purchaseIntentId) ??
      readString(source.intent_id ?? source.intentId) ??
      readString(payload.id) ??
      '',
    paymentRequired: readBoolean(source.payment_required ?? source.paymentRequired),
    clientSecret: readString(source.client_secret ?? source.clientSecret),
    stripeIntentType,
    stripeSubscriptionId: readString(source.stripe_subscription_id ?? source.stripeSubscriptionId),
    stripePaymentIntentId: readString(source.stripe_payment_intent_id ?? source.stripePaymentIntentId),
    stripeSetupIntentId: readString(source.stripe_setup_intent_id ?? source.stripeSetupIntentId),
    stripe:
      stripePublishableKey || stripeAccountId || stripeAccountType
        ? {
            publishableKey: stripePublishableKey,
            accountId: stripeAccountId,
            accountType: stripeAccountType,
          }
        : null,
  };
};

const normalizeCheckoutConfirm = (input: unknown): CustomerSubscriptionCheckoutConfirmResponse => {
  const payload = resolvePayload(input);
  const subscription = readRecord(payload.subscription ?? payload.current_subscription);

  return {
    subscriptionStatus: readString(subscription.display_status ?? subscription.status) ?? 'active',
    message: readString(payload.message),
  };
};

class SubscriptionCheckoutService {
  async getCheckoutContext(params?: { planId?: string | null; priceId?: string | null }): Promise<CustomerSubscriptionCheckoutContext> {
    const url = new URL('/api/v1/customer/subscription/checkout-context', 'http://placeholder.local');
    if (params?.planId) {
      url.searchParams.set('plan_id', params.planId);
    }
    if (params?.priceId) {
      url.searchParams.set('price_id', params.priceId);
    }
    const response = await apiClient.get<unknown>(`${url.pathname}${url.search}`);
    return normalizeCheckoutContext(response.data);
  }

  async createCheckoutIntent(
    payload: CustomerSubscriptionCheckoutIntentRequest,
  ): Promise<CustomerSubscriptionCheckoutIntent> {
    const response = await apiClient.post<unknown>('/api/v1/customer/subscription/checkout-intent', payload);
    return normalizeCheckoutIntent(response.data);
  }

  async confirmCheckout(
    payload: CustomerSubscriptionCheckoutConfirmRequest,
  ): Promise<CustomerSubscriptionCheckoutConfirmResponse> {
    const response = await apiClient.post<unknown>('/api/v1/customer/subscription/checkout-confirm', payload);
    return normalizeCheckoutConfirm(response.data);
  }
}

export const subscriptionCheckoutService = new SubscriptionCheckoutService();
